
import { defineComponent, computed, ref, watch, getCurrentInstance } from 'vue';
import { getLogDetail, getProductionLog } from '@/api/production-demand';
import {
  DELIVERY_TYPE_LIST,
  EMERGENCY_LEVEL_LIST,
  PICK_WAY_LIST,
  PRODUCTION_DEMAND_STATUS_LIST,
  PRODUCTION_PATTERN_LIST,
  PRODUCT_CHAIN_LIST,
  YES_OR_NO_NUMBER_ENUM,
  YES_OR_NO_NUMBER_LIST,
} from '@/constant/production-demand';
import ShowTable from '../show-table/main.vue';
import type {
  IProductionOrderLogsRes,
  IProductionOrderLogsOriginalLog,
  IProductionOrderLogsDiffListItem,
} from '@/api/production-demand/type';
import { useDictionary } from '@/hooks/use-dict';

export default defineComponent({
  name: 'Logger',
  components: { ShowTable },
  props: {
    modelValue: Boolean,
    orderId: String,
    type: String,
  },
  setup(props, { emit }) {
    // 内部标记是否不执行watch
    let stopWatch = false;
    const visible = computed({
      get() {
        return !!props.modelValue;
      },
      set(val: boolean) {
        emit('update:modelValue', val);
      },
    });
    const list = ref<IProductionOrderLogsRes>([]);

    const handleGetApi = async (orderId: string) => {
      // type = PRODUCTION: 生产信息日志接口，不传则默认订单信息日志接口
      const logApi = props.type === 'PRODUCTION' ? getProductionLog : getLogDetail;
      const { data } = await logApi(orderId);
      list.value = data ?? [];
    };

    /**
     * 显示收获地址
     */
    const getAddress = (
      info: IProductionOrderLogsOriginalLog['updateOrder'] | IProductionOrderLogsOriginalLog['oldOrder'],
    ) => {
      // eslint-disable-next-line max-len
      return `${info?.postProvince || ''}${info?.postCity || ''}${info?.postArea || ''}${info?.postStreet || ''}${info?.postAddressDetail || ''}`;
    };

    watch(visible, () => {
      if (stopWatch) {
        stopWatch = false;
        return;
      }
      if (visible.value && props.orderId) {
        handleGetApi(props.orderId);
      }
    }, {
      immediate: true,
    });

    /**
     * 开放外部直接使用
     */
    const show = (order: string) => {
      stopWatch = true;
      handleGetApi(order);
      visible.value = true;
    };

    const globalProperties = getCurrentInstance()?.appContext.config?.globalProperties;
    const getEnumLabel = globalProperties?.$filters?.getEnumLabel;

    // 请求云工厂字典接口，税点、支付方式、接单方式
    const { batchDictListMap } = useDictionary([
      'pims_payment_method',
      'pims_receiving_entity',
    ]);
    // 支付方式 取一级
    const paymentMethodList = computed(() => {
      return batchDictListMap.value?.pims_payment_method
        ?.filter((item) => {
          return item.isEnable === YES_OR_NO_NUMBER_ENUM.YES
            && item.valueParentCode === '';
        }).map((it) => {
          return {
            label: it.value,
            value: it.valueCode,
          };
        }) ?? [];
    });
    // 接单方式，取一级
    const receivingEntityList = computed(() => {
      return batchDictListMap.value?.pims_receiving_entity
        ?.filter((item) => {
          return item.isEnable === YES_OR_NO_NUMBER_ENUM.YES
            && item.valueParentCode === '';
        }).map((it) => {
          return {
            label: it.value,
            value: it.valueCode,
          };
        }) ?? [];
    });

    return {
      visible,
      list,
      PRODUCTION_DEMAND_STATUS_LIST,
      getAddress,
      getLogItem: (it: IProductionOrderLogsDiffListItem, type: 'oldValue' | 'newValue') => {
        const { path, logName, diffValue } = it;
        const key = path?.slice(1);
        const keyMap = {
          // productChain: PRODUCT_CHAIN_LIST,
          // 支付方式 字典
          paymentType: paymentMethodList.value,
          // 接单主体 字典
          orderMainstay: receivingEntityList.value,
          // 开具发票
          invoice: YES_OR_NO_NUMBER_LIST,
          // 允许溢出
          allowOverflow: YES_OR_NO_NUMBER_LIST,
          // 生产模式
          productionPattern: PRODUCTION_PATTERN_LIST,
          // 领料方式
          pickupFabricWay: PICK_WAY_LIST,
          // 交付方式
          deliveryType: DELIVERY_TYPE_LIST,
          // 紧急程度
          emergencyLevel: EMERGENCY_LEVEL_LIST,
        };
        if (Object.keys(keyMap).includes(key!)) {
          return `${logName}：${getEnumLabel?.(keyMap[key as keyof typeof keyMap], diffValue?.[type] || '')}`;
        }
        // 生产环节 本地枚举
        if (key === 'productChain') {
          const arr = diffValue?.[type]?.split(',') ?? [];
          return `${logName}：${arr?.map(v => `${getEnumLabel?.(PRODUCT_CHAIN_LIST, v)}`).join()}`;
        }
        return `${logName}：${diffValue[type]}`;
      },
      show,
    };
  },
});
